import { COLORS, GRID_COLORS } from '../config/constants';
import { getGridLineRange, type ViewportState } from './viewport';

type GridLineExtent = 'viewport' | 'padded';

export interface GridRenderOptions {
  width: number;
  height: number;
  gridSize: number;
  inverted: boolean;
  viewport: ViewportState;
  enabled?: boolean;
  paddingCells?: number;
  lineExtent?: GridLineExtent;
}

export interface GridAxisOptions {
  inverted: boolean;
  viewport: ViewportState;
  enabled?: boolean;
  size?: number;
  lineWidth?: number;
}

export const fillCanvasBackground = (
  ctx: CanvasRenderingContext2D,
  width: number,
  height: number,
  inverted: boolean
): void => {
  ctx.fillStyle = inverted ? COLORS.black : COLORS.white;
  ctx.fillRect(0, 0, width, height);
};

export const renderGridLines = (ctx: CanvasRenderingContext2D, options: GridRenderOptions): void => {
  const {
    width,
    height,
    gridSize,
    inverted,
    viewport,
    enabled = true,
    paddingCells = 0,
    lineExtent = 'viewport',
  } = options;
  if (!enabled) return;

  const range = getGridLineRange(width, height, viewport, gridSize, paddingCells);
  const lineMinX = lineExtent === 'padded' ? range.startX : range.minX;
  const lineMaxX = lineExtent === 'padded' ? range.endX : range.maxX;
  const lineMinY = lineExtent === 'padded' ? range.startY : range.minY;
  const lineMaxY = lineExtent === 'padded' ? range.endY : range.maxY;

  ctx.strokeStyle = inverted ? GRID_COLORS.lineLight : GRID_COLORS.lineDark;
  ctx.lineWidth = 1 / viewport.scale;

  ctx.beginPath();
  for (let x = range.startX; x <= range.endX; x += gridSize) {
    ctx.moveTo(x, lineMinY);
    ctx.lineTo(x, lineMaxY);
  }
  for (let y = range.startY; y <= range.endY; y += gridSize) {
    ctx.moveTo(lineMinX, y);
    ctx.lineTo(lineMaxX, y);
  }
  ctx.stroke();
};

export const renderGridAxis = (ctx: CanvasRenderingContext2D, options: GridAxisOptions): void => {
  const { inverted, viewport, enabled = true, size = 20, lineWidth = 2 } = options;
  if (!enabled) return;

  ctx.strokeStyle = inverted ? GRID_COLORS.axisLight : GRID_COLORS.axisDark;
  ctx.lineWidth = lineWidth / viewport.scale;
  ctx.beginPath();
  ctx.moveTo(-size, 0);
  ctx.lineTo(size, 0);
  ctx.moveTo(0, -size);
  ctx.lineTo(0, size);
  ctx.stroke();
};
